import { Autocomplete, Box, Grid, InputAdornment, TextField } from "@mui/material";
import { CloseIcon, SearchIcon } from "components/Icons";
import { debounce, omit } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { search, setSearchApplicationQuery } from "store/application/student-applications";

const formStatusOptions = [
    { name: 'Not Started', value: 'not_started' },
    { name: 'Incomplete', value: 'incomplete' },
    { name: 'Expired', value: 'expired' },
    { name: 'Completed', value: 'completed' },
]

const fileStatusOptions = [
    { name: 'All Approved', value: 'approved' },
    { name: 'Any No Files', value: 'no_file' },
    { name: 'Any In Review', value: 'pending' },
    { name: 'Any Rejected', value: 'rejected' },
    { name: 'Expired', value: 'expired' },
]

export default function ApplicationRecordsListFilter({ clientSchools, gradeLevel, types, academicYears = [] }) {
    const dispatch = useDispatch()
    const { query } = useSelector(state => state.application.studentApplications)
    const { t } = useTranslation()
    const { district } = useSelector(state => state.session)
    const showStatusOptions = query?.status === 'inprogress'

    const filterNullValues = obj => {
        let temp = {}
        Object.keys(obj).forEach(key => {
            obj[key] !== null ? (temp[key] = obj[key]) : null
        })
        return temp
    }

    const applyFilter = async (key, value) => {
        let filteredQuery = { ...filterNullValues(query), [key]: value, offset: 0 };
        
        if (key === 'status' && value !== 'inprogress') {
            filteredQuery = omit(filteredQuery, ['fileStatus', 'formStatus']);
        }
        
        if ((typeof value === 'string' && (value.trim().length > 2 || value === 'K' || value === 'Pre-K')) || (+value && value > 0)) {
            dispatch(search(filteredQuery))
        } else if (!value || value === '' || typeof value !== 'string') {
            let omittedQuery = filterNullValues(omit(query, key));
            if (key === 'status' || !showStatusOptions) {
                omittedQuery = omit(omittedQuery, ['fileStatus', 'formStatus']);
            }
            dispatch(search({ ...omittedQuery, offset: 0 }))
        }
    }

    const delayedSearch = useMemo(() => {
        return debounce((que, callback) => callback('que', que), 500)
    }, [dispatch])


    if (!types || !clientSchools ) return null

    const selectedFormStatus = formStatusOptions.find(opt => opt.value === query?.formStatus) || null;
    const selectedFileStatus = fileStatusOptions.find(opt => opt.value === query?.fileStatus) || null;
    const selectedStatus = types?.find(opt => opt.code === query?.status) || null;
    const selectedYear = academicYears.find(opt => opt.yearApp === query?.yearApp) || null;

    return (
        <Box overflow='auto'>
            <Grid container alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={showStatusOptions ? 6 : 4}>
                    <TextField
                        size="small"
                        sx={{ width: '100%' }}
                        placeholder={t('Search by name, application #')}
                        value={query?.que || ''}
                        onChange={e => {
                            if (query?.que !== e.target.value) {
                                if (e.target.value?.length > 0) {
                                    dispatch(setSearchApplicationQuery({ ...query, que: e.target.value }))
                                } else {
                                    dispatch(setSearchApplicationQuery({ ...query, que: null }))
                                }
                            }
                            if (e.target.value?.length > 0) {
                                delayedSearch(e.target.value, applyFilter)
                            } else {
                                delayedSearch(null, applyFilter)
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <CloseIcon
                                    onClick={() => {
                                        dispatch(setSearchApplicationQuery({ ...query, que: null }))
                                        delayedSearch(null, applyFilter)
                                    }}
                                    sx={{ '&:hover': { cursor: 'pointer' }, fontSize: 'small' }}
                                />
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Autocomplete
                        options={gradeLevel}
                        getOptionLabel={option => option?.name || ''}
                        onChange={(_, value) => {
                            dispatch(setSearchApplicationQuery({ ...query, gradeApp: value?.id || null }));
                            applyFilter('gradeApp', value?.id || null);
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Grade Applying" variant="outlined" size="small" />
                        )}
                    />
                </Grid>
                {district?.isServiceProvider && (
                    <Grid item xs={12} md={3} >
                        <Autocomplete
                            options={clientSchools}
                            getOptionLabel={option => option?.abbreviation || ''}
                            onChange={(_, value) => {
                                dispatch(setSearchApplicationQuery({ ...query, schoolId: value?.id || null }));
                                applyFilter('schoolId', value?.id || null);
                            }}
                            sx={{ flex: 1 }}
                            renderInput={(params) => (
                                <TextField {...params} label="School" variant="outlined" size="small" />
                            )}
                        />
                    </Grid>
                )}
                <Grid item xs={12} md={district?.isServiceProvider ? 2 : 3}>
                    <Autocomplete
                        options={academicYears}
                        getOptionLabel={option => option?.label || ''}
                        isOptionEqualToValue={(option, val) => option.yearApp === val?.yearApp}
                        value={selectedYear}
                        onChange={(_, value) => {
                            dispatch(setSearchApplicationQuery({ ...query, yearApp: value?.yearApp || null }));
                            applyFilter('yearApp', value?.yearApp || null);
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Enrollment Year" variant="outlined" size="small" />
                        )}
                    />
                </Grid>
                {!district?.isServiceProvider && (
                    <Grid item xs={12} md={showStatusOptions ? 4 : 2}>
                        <Autocomplete
                            options={types}
                            value={selectedStatus}
                            getOptionLabel={option => option?.name || ''}
                            onChange={(event, value) => {
                                dispatch(setSearchApplicationQuery({ 
                                    ...query, 
                                    status: value?.code || null,
                                    formStatus: null,
                                    fileStatus: null
                                }));
                                applyFilter('status', value?.code || null);
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Status" variant="outlined" size="small"  />
                            )}
                        />
                    </Grid>
                )}
                {showStatusOptions && (
                    <>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                options={formStatusOptions}
                                value={selectedFormStatus}
                                getOptionLabel={option => option?.name || ''}
                                onChange={(event, value) => {
                                    dispatch(setSearchApplicationQuery({ ...query, formStatus: value?.value || null }));
                                    applyFilter('formStatus', value?.value || null);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Form Status" variant="outlined" size="small" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                options={fileStatusOptions}
                                value={selectedFileStatus}
                                getOptionLabel={option => option?.name || ''}
                                onChange={(event, value) => {
                                    dispatch(setSearchApplicationQuery({ ...query, fileStatus: value?.value || null }));
                                    applyFilter('fileStatus', value?.value || null);
                                }}
                                disabled={query?.formStatus !== 'completed'}
                                renderInput={(params) => (
                                    <TextField {...params} label="File Status" variant="outlined" size="small" />
                                )}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    )
}